import { useState } from 'react';
import { useUploadVendorInvoice } from '../useUploadVendorInvoice';
import { fetchVendorInvoiceUpload } from '../api';
import type { ExtractedInvoiceLine } from '../types';

// How long to keep polling a single upload before giving up and telling the
// user to fall back to manual entry — generous, since a dense invoice image
// can legitimately take tens of seconds to read.
const POLL_TIMEOUT_MS = 120_000;
const POLL_INTERVAL_MS = 2_000;

// There's no real sub-progress the server can report mid-read — the AI call
// is one request/response, not something that streams partial results — so
// this can't show a literal step-by-step trace. What it CAN do honestly is
// name what that single call is actually extracting, and get more
// reassuring the longer it runs, rather than leaving one static "Reading…"
// message up for up to two minutes with no sign anything is still moving.
const PROGRESS_STAGES: Array<{ afterMs: number; message: string }> = [
  { afterMs: 0, message: 'Invoice received — reading manufacturer, batch and expiry for each line…' },
  { afterMs: 5_000, message: 'Extracting quantities, free stock and pricing from each line…' },
  { afterMs: 12_000, message: 'Cross-checking each line\'s quantity against the invoice\'s own printed amount…' },
  { afterMs: 25_000, message: 'Still working — a dense multi-line invoice can take up to a minute to read…' },
  { afterMs: 60_000, message: 'Still working — this is an unusually long read, but it hasn\'t failed; hang tight…' },
];

/** Exported so ProcurementInvoiceQRModal.tsx's "reading" indicator shows the
 * same staged messages while it polls the phone-upload session, instead of
 * a second, differently-worded copy. */
export function progressMessage(elapsedMs: number): string {
  let msg = PROGRESS_STAGES[0]!.message;
  for (const stage of PROGRESS_STAGES) {
    if (elapsedMs >= stage.afterMs) msg = stage.message;
  }
  return msg;
}

function describeExtraction(lines: ExtractedInvoiceLine[]): string {
  const flagged = lines.filter((l) => l.amount_mismatch).length;
  return `${lines.length} line${lines.length > 1 ? 's' : ''} read from the invoice — review and resolve each product before saving.`
    + (flagged ? ` ${flagged} line${flagged > 1 ? 's are' : ' is'} flagged (highlighted below) — the read quantity didn't match the invoice's own printed amount, double-check it.` : '');
}

/**
 * Shared "upload/scan a vendor invoice, wait for the AI read, apply the
 * result" flow behind both GoodsReceiptPanel's standalone GRN and
 * PurchaseOrdersPanel's "Mark Received" flow — one engine, two front doors,
 * same reasoning as GrnLineEditor.
 *
 * POST /vendor-invoices responds as soon as the file is stored (status
 * 'processing') rather than blocking on the AI read — a vision call over a
 * dense invoice image can legitimately take 10-40s, long enough to trip a
 * hosting platform's own front-door proxy timeout if the request just sat
 * there waiting. So this polls GET /vendor-invoices/:id (same endpoint the
 * scan-via-phone flow's session status already surfaces) until it settles,
 * updating the visible notice at each tick so the wait doesn't read as stalled.
 */
export function useInvoiceUploadFlow(onExtracted: (lines: ExtractedInvoiceLine[]) => void) {
  const [uploadNotice, setUploadNotice] = useState('');
  const [readingInvoice, setReadingInvoice] = useState(false);
  const uploadInvoice = useUploadVendorInvoice();

  const applyOrExplain = (lines: ExtractedInvoiceLine[]) => {
    if (!lines.length) { setUploadNotice('No line items were recognized in that invoice — enter the details manually below.'); return; }
    onExtracted(lines);
    setUploadNotice(describeExtraction(lines));
  };

  const pollUploadStatus = (id: number, startedAt: number) => {
    fetchVendorInvoiceUpload(id)
      .then((upload) => {
        const elapsed = Date.now() - startedAt;
        if (upload.status === 'processing') {
          if (elapsed > POLL_TIMEOUT_MS) {
            setReadingInvoice(false);
            setUploadNotice('Still reading this invoice — check back in a moment, or enter the details manually below.');
            return;
          }
          setUploadNotice(progressMessage(elapsed));
          setTimeout(() => pollUploadStatus(id, startedAt), POLL_INTERVAL_MS);
          return;
        }
        setReadingInvoice(false);
        if (upload.status === 'done' && upload.extracted_data) applyOrExplain(upload.extracted_data);
        else setUploadNotice(upload.error_message || 'Could not read this invoice automatically — enter the details manually below.');
      })
      .catch((e: unknown) => {
        setReadingInvoice(false);
        setUploadNotice(e instanceof Error ? e.message : String(e));
      });
  };

  /** Desk file-picker upload. `poId` links the upload record to a PO for
   * traceability when receiving against one (see "Mark Received"). */
  const handleFilePick = (file: File | undefined, poId?: number) => {
    if (!file) return;
    setReadingInvoice(true);
    setUploadNotice(`Uploading ${file.name}…`);
    uploadInvoice.mutate({ file, poId }, {
      onSuccess: (upload) => {
        if (upload.status === 'processing') {
          setUploadNotice(progressMessage(0));
          pollUploadStatus(upload.id, Date.now());
        } else {
          setReadingInvoice(false);
          if (upload.status === 'done' && upload.extracted_data) applyOrExplain(upload.extracted_data);
          else setUploadNotice(upload.error_message || 'Could not read this invoice automatically — enter the details manually below.');
        }
      },
      onError: (e) => {
        setReadingInvoice(false);
        setUploadNotice(e instanceof Error ? e.message : String(e));
      },
    });
  };

  /** Scan-via-phone QR modal's onReceived — extraction already finished
   * server-side by the time this fires, so it's a straight pass-through. */
  const handleReceived = (lines: ExtractedInvoiceLine[]) => applyOrExplain(lines);

  return {
    uploadNotice,
    clearNotice: () => setUploadNotice(''),
    isBusy: uploadInvoice.isPending || readingInvoice,
    handleFilePick,
    handleReceived,
  };
}
